import React, { useState } from 'react';
import { useStringsContext } from '../../../../contexts/strings';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { SuperAdminUser } from '../../../../types';
import { useSnackbar } from 'notistack';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';

interface Props {
  isOpen: boolean;
  selectedUser: SuperAdminUser;
  onClose: () => void;
}

const RemoveUserDialog = ({ onClose, isOpen, selectedUser }: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useUserAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onDelete = async () => {
    setIsSubmitting(true);

    try {
      const { uid } = selectedUser;

      const response = await adminApi.delete({
        path: `/users/backend-user`,
        auth: await currentUser?.getIdToken(),
        body: { userId: uid },
      });
      const responseData = await response.json();

      setIsSubmitting(false);

      const isError = responseData.error;
      if (isError) {
        throw new Error();
      }

      enqueueSnackbar(strings.messages.deleteSuccess, {
        variant: 'success',
      });

      onClose();
    } catch (error) {
      enqueueSnackbar(strings.messages.deleteError, { variant: 'error' });
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} scroll="paper" fullWidth>
      <DialogTitle>
        {strings.usersAndPermissions.removeUser.replace('$email', selectedUser.email)}
      </DialogTitle>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {strings.squad.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isSubmitting}
          startIcon={<DeleteIcon />}
          onClick={onDelete}
        >
          {strings.squad.yesRemove}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveUserDialog;
